import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import LinkedDevicesConfig from './LinkedDevicesConfig';
import PortConfigComponent from './PortConfig';
import {
  DEFAULT_LOAD_RESET_MINUTES,
  DIRECTION_OPTIONS,
  LOAD_RESET_AFTER_SET_TIME,
  LOAD_RESET_WHEN_PICKED,
  WeighbridgeConfig
} from './WeighbridgePeripheralConfig';

const DEFAULT_NAME = 'Loader';
const DEFAULT_UNIT = 't';
const POSITIONAL_PROTOCOL = 'Positional';
const LOADRITE_PROTOCOL = 'Loadrite2180';
// Mirrors Weightrax.Domain.Enums.WeightModeEnum.Net.
const NET_WEIGHT_MODE = 2;

const UNIT_OPTIONS = [
  { value: 't', label: 'Tonnes' },
  { value: 'kg', label: 'Kilograms' }
];

const LOAD_RESET_OPTIONS = [
  { value: LOAD_RESET_WHEN_PICKED, label: 'When picked' },
  { value: LOAD_RESET_AFTER_SET_TIME, label: 'After a set time' }
];

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** The name and unit the reader takes from the first weight config, written in camelCase (this editor) or PascalCase (the Local API). */
function readScale(value: WeighbridgeConfig): { name: string; unit: string } {
  const row = asRecord(value.weightConfigs[0]);
  const unit = asRecord(row.unit ?? row.Unit);

  return {
    name: asText(row.name ?? row.Name),
    unit: asText(unit.mapsTo ?? unit.MapsTo) || DEFAULT_UNIT
  };
}

function scaleRow(name: string, unit: string) {
  return { name, unit: { mapsTo: unit } };
}

/**
 * What a loader scale is saved as. It only ever weighs the load, and it can only be read by a loader scale protocol, whatever
 * the type's default configuration says. It sends no unit, so it always needs a weight config to hold one.
 */
export function toLoaderScaleConfig(value: WeighbridgeConfig): WeighbridgeConfig {
  const { name, unit } = readScale(value);

  return {
    ...value,
    weightMode: NET_WEIGHT_MODE,
    protocol: value.protocol && value.protocol !== POSITIONAL_PROTOCOL ? value.protocol : LOADRITE_PROTOCOL,
    // Left empty or at nothing while being typed, which would reset a load as soon as it arrives.
    loadResetMinutes: value.loadResetMinutes >= 1 ? value.loadResetMinutes : DEFAULT_LOAD_RESET_MINUTES,
    weightConfigs: [scaleRow(name || DEFAULT_NAME, unit)]
  };
}

type LoaderScalePeripheralConfigProps = {
  value: WeighbridgeConfig;
  onChange: (next: WeighbridgeConfig) => void;
  disabled?: boolean;
};

/**
 * A loader scale reports completed loads rather than a feed read at set positions, so there are no feed or weight positions to
 * configure here: the peripheral type sets how it is read, and only the connection and how it shows on the POS are left.
 */
export default function LoaderScalePeripheralConfig({ value, onChange, disabled = false }: LoaderScalePeripheralConfigProps) {
  const { name, unit } = readScale(value);

  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Loader Scale Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Each completed load shows on the POS as a net weight, for the operator to pick into a line.
        </Typography>
      </div>

      <Grid container spacing={2}>
        <PortConfigComponent value={value.portConfig} onChange={(next) => onChange({ ...value, portConfig: next })} disabled={disabled} />

        <Grid size={{ xs: 12 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Display name"
                placeholder={DEFAULT_NAME}
                value={name}
                onChange={(event) => onChange({ ...value, weightConfigs: [scaleRow(event.target.value, unit)] })}
                helperText="Shown on the POS weight card"
                disabled={disabled}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                fullWidth
                select
                label="Unit"
                value={unit}
                onChange={(event) => onChange({ ...value, weightConfigs: [scaleRow(name, event.target.value)] })}
                helperText="As set on the scale"
                disabled={disabled}
              >
                {UNIT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                fullWidth
                select
                label="Direction"
                value={value.direction}
                onChange={(event) => onChange({ ...value, direction: Number(event.target.value) })}
                disabled={disabled}
              >
                {DIRECTION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth
                select
                label="Reset load to 0"
                value={value.loadReset}
                onChange={(event) => onChange({ ...value, loadReset: Number(event.target.value) })}
                helperText="When the POS stops offering a completed load"
                disabled={disabled}
              >
                {LOAD_RESET_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {value.loadReset === LOAD_RESET_AFTER_SET_TIME && (
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minutes"
                  value={value.loadResetMinutes || ''}
                  onChange={(event) => onChange({ ...value, loadResetMinutes: Number(event.target.value) })}
                  slotProps={{ htmlInput: { min: 1 } }}
                  helperText="From when the load completes"
                  disabled={disabled}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <LinkedDevicesConfig
            value={value.linkedDevices}
            onChange={(next) => onChange({ ...value, linkedDevices: next })}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
